import "./style.scss";
import { FC, useState, useEffect, useRef, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import LogoSvg from "@/src/svg/default/LogoSvg";
import BtnDefault from "../../../../BtnDefault/BtnDefault";
import CustomSwitch from "../../../../CustomUtils/CustomSwitch/CustomSwitch";
import { AOPMaltaAdmissionData, AOPPolandAdmissionData, AOPUkraineAdmissionData } from "@/src/types/admissionOffice";
import { Editor } from "@tinymce/tinymce-react";
import type { Editor as TinyMCEEditor } from "tinymce";
import { splitHtmlByBlocks, splitHtmlByBlocksWithFontConvert } from "../../../converterPtToRem";
import { TINYMCE_EDITOR_CONTENT_STYLE, TINYMCE_EDITOR_HEIGHT, TINYMCE_FONT_SIZE_FORMATS } from "../../../tinyMceEditorDisplayStyle";
import AutocompleteInput from "@/src/components/AutocompleteInput/AutocompleteInput";
import {
  buildIntakeOptionsWithStored,
  getIntakeDisplayLabel,
  resolveDefaultIntakeValue,
} from "@/src/utils/intakeCalc";
import { STANDARD_DEGREE_LEVEL_VALUES } from "@/src/utils/admissionOffice/buildDegreeSelectOptions";
import { templatesMaltaEnFormOfEducationSettings } from "@/src/constants/AdmissionOffice/Malta/AOMaltaEnConsts";
import { FORM_OF_EDUCATION_DISTANCE, FORM_OF_EDUCATION_FULL_TIME, MALTA_FORM_OF_EDUCATION_LABELS } from "@/src/constants/AdmissionOffice/Malta/maltaFormOfEducationOptions";
import { MEDIUM_OF_INSTRUCTION_LABELS, normalizeMediumOfInstructionValue } from "@/src/constants/AdmissionOffice/mediumOfInstructionOptions";
import { ADMISSION_SEMESTER_LABELS, normalizeAdmissionSemesterValue } from "@/src/constants/AdmissionOffice/admissionSemesterOptions";
import {
  MALTA_OFFERED_COURSES_BY_LABEL,
  MALTA_OFFERED_COURSE_LABELS,
} from "@/src/constants/AdmissionOffice/Malta/maltaOfferedCoursesConfig";
import { applyMaltaDistanceOnlineFormDefaults } from "@/src/constants/AdmissionOffice/Malta/maltaDistanceAdmissionConfig";
import {
  DEFAULT_MALTA_FULL_TIME_FEE_VALUES,
  applyMaltaFormOfEducationUnselectedDefaults,
  applyMaltaFullTimeOnCampusFormDefaults,
} from "@/src/constants/AdmissionOffice/Malta/maltaFullTimeAdmissionConfig";
import {
  applyMaltaOfferedCourseSelection,
  applyMaltaOfferedCourseCertificationEqualization,
  applyMaltaOfferedCoursePreparatoryDepartment,
  clearMaltaOfferedCourseSelection,
  isMaltaPreparatoryOfferedCourse,
} from "@/src/utils/admissionOffice/malta/applyMaltaOfferedCourseSelection";
import {
  buildMaltaFullTimeAdmissionBody,
  calcMaltaFullTimeFirstYearTotal,
  formatMaltaMoneyValue,
} from "@/src/utils/admissionOffice/malta/buildMaltaFullTimeAdmissionBody";
import {
  buildMaltaDistanceAdmissionBody,
  calcMaltaDistanceFirstYearTotal,
} from "@/src/utils/admissionOffice/malta/buildMaltaDistanceAdmissionBody";
import MaltaFullTimeFeeField from "./MaltaFullTimeFeeField";
import AOPIdSourceRow, { AOP_ID_SOURCE_DEFAULT_VALUES } from "@/src/components/AdmissionsOfficePage/shared/AOPIdSourceRow";
import { useAutocomplete } from "@/src/hooks/useAutocomplete";
import { admissionOfficeRequests } from "@/src/api/admissionOfficeDocCreateRequests";
import { Nationalities } from "@/src/constants/globalConsts";
import { ADMISSION_LETTER_BASIC_REGISTRY } from "../admissionLetterBasicRegistry";
import type { AdmissionOfficeCountry } from "../../../admissionOfficeCountry";
import { admissionOfficeWhoseDoc } from "@/src/constants/AdmissionOffice/admissionsOfficeConsts";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import { useDispatch } from "react-redux";
import { setIsPreloyder } from "@/src/store/slices/Preloyder/PreloyderSlice";
import { useAdmissionTaskPrefill, type AdmissionTaskPrefillPayload } from "@/src/hooks/useAdmissionTaskPrefill";
import { useAOPIdSourceFields } from "@/src/hooks/useAOPIdSourceFields";
import { useAdmissionGeneratedDocumentActions } from "@/src/hooks/useAdmissionGeneratedDocumentActions";
import { useSyncWithQRPreview } from "@/src/hooks/useSyncWithQRPreview";
import AOPGeneratedDocumentToolbar from "@/src/components/AdmissionsOfficePage/AOPGeneratedDocumentToolbar";
import {
  admissionLetterTaskPrefillMapping,
  syncCourseAndNationalityAutocompletes,
  syncFormOfEducationAutocomplete,
  syncMediumOfInstructionAutocomplete,
} from "@/src/constants/AdmissionOffice/admissionTaskPrefillMappings";
import { joinSelectedCourse, splitSelectedCourse } from "@/src/utils/admissionOffice/splitSelectedCourse";
interface IAOPFormValues {
  whose_doc: string;
  addInvoice?: boolean;
  currentDate: string;
  fullName: string;
  fullNameNat?: string; // new
  dateOfBirth: string;
  nationality: string;
  formOfEducation?: string; // new
  admissionSemester?: string; // new
  intake?: string; // new
  category?: string; // new
  offeredCourse?: string;
  course: string;
  selectedCourse: string;
  degree?: string;
  mediumOfInstruction: string;
  duration: string;
  tuitionFee?: string; // new
  medicalInsurance?: string; // new
  residencePermit?: string; // new
  otherExpenses?: string; // new
  acommodation?: string; // new
  admissionFee?: string; // new
  transfer?: string; // new
  completeOneYear?: string; // new
  certificationEqualization?: string;
  preparatoryDepartment?: string;
  /** Letter "S" → task `tuition_fee_flag` */
  tuitionFeeFlag?: boolean;
  tuitionFeeVisible?: boolean;
  acommodationVisible?: boolean;
  medicalInsuranceVisible?: boolean;
  residencePermitVisible?: boolean;
  admissionFeeVisible?: boolean;
  otherExpensesVisible?: boolean;
  transferVisible?: boolean;
  certificationEqualizationVisible?: boolean;
  preparatoryDepartmentVisible?: boolean;
  completeOneYearVisible?: boolean;
  showSecondYear?: boolean;
  showTransferFeeNotice?: boolean;
  showAdmissionLetterNotice?: boolean;
  showFirstYearPaymentConfirmation?: boolean;
  invoiceBody: string;
  signature: string;
  coursePrefix: string;
  task_id?: string;
  withQR?: boolean;
  student_id?: string;
  fieldEmail?: boolean;
  userEmail?: string;
}

interface AOPConfigProps {
  country: AdmissionOfficeCountry;
  dataForGeneration: AOPMaltaAdmissionData;
  setDataForGeneration: React.Dispatch<React.SetStateAction<AOPMaltaAdmissionData>>;
  setBodyForPrev: React.Dispatch<React.SetStateAction<AOPMaltaAdmissionData>>;
  setShowInvoiceInPreview: React.Dispatch<React.SetStateAction<boolean>>;
  setWithQRForPreview: React.Dispatch<React.SetStateAction<boolean>>;
}

type FeeFieldKey =
  | "tuitionFee"
  | "medicalInsurance"
  | "residencePermit"
  | "otherExpenses"
  | "acommodation"
  | "admissionFee"
  | "transfer"
  | "completeOneYear";

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const FEE_LINE_CONFIG: Array<{ field: FeeFieldKey; labels: string[] }> = [
  { field: "tuitionFee", labels: ["Tuition Fee"] },
  { field: "medicalInsurance", labels: ["Medical Insurance"] },
  { field: "residencePermit", labels: ["Temporary Residence Permission", "Residence Permit"] },
  { field: "otherExpenses", labels: ["Other Expenses"] },
  { field: "acommodation", labels: ["Accommodation confirmation letter", "Accommodation"] },
  { field: "admissionFee", labels: ["Admission Fee"] },
  { field: "transfer", labels: ["Transfer Charges", "Transfer"] },
  { field: "completeOneYear", labels: ["Complete first year expenses", "Complete One Year"] },
];

const SUPPORTED_CURRENCIES = ["EUR", "USD", "USDT", "GBP", "AED"] as const;
const CURRENCY_PATTERN = SUPPORTED_CURRENCIES.join("|");

const buildLabelRegex = (label: string) => {
  const escapedLabel = escapeRegExp(label);
  return new RegExp(`(${escapedLabel}[\\s\\S]{0,260}?:\\s*(?:<[^>]*>\\s*)*)([\\d\\s.,]+)(\\s*(?:${CURRENCY_PATTERN})[^<\\n\\r]*)`, "i");
};

const replaceFeeValueInContent = (html: string, labels: string[], nextValue: string) => {
  if (!html || !nextValue.trim()) return html;
  let nextHtml = html;
  labels.forEach((label) => {
    const matcher = buildLabelRegex(label);
    nextHtml = nextHtml.replace(matcher, `$1${nextValue.trim()}$3`);
  });
  return nextHtml;
};

const extractFeeValueFromContent = (html: string, labels: string[]) => {
  if (!html) return "";
  for (const label of labels) {
    const matcher = buildLabelRegex(label);
    const match = html.match(matcher);
    if (match?.[2]) {
      return match[2].trim();
    }
  }
  return "";
};

const MALTA_FORM_OF_EDUCATION_FEE_FIELDS = [
  "acommodation",
  "residencePermit",
  "admissionFee",
  "otherExpenses",
  "certificationEqualization",
] as const;

const MALTA_FORM_OF_EDUCATION_FEE_LINE_CONFIG: Array<{
  field: (typeof MALTA_FORM_OF_EDUCATION_FEE_FIELDS)[number];
  labels: string[];
}> = [
  { field: "residencePermit", labels: ["Temporary Residence Permission", "Residence Permit"] },
  { field: "otherExpenses", labels: ["Other Expenses"] },
  { field: "acommodation", labels: ["Accommodation confirmation letter", "Accommodation"] },
  { field: "admissionFee", labels: ["Admission Fee"] },
];

const populateMaltaFormOfEducationFeeFields = (
  templateContent: string,
  setValue: (name: keyof typeof DEFAULT_MALTA_FULL_TIME_FEE_VALUES, value: string, options?: { shouldDirty?: boolean }) => void,
) => {
  MALTA_FORM_OF_EDUCATION_FEE_FIELDS.forEach((fieldKey) => {
    setValue(fieldKey, "", { shouldDirty: false });
  });

  MALTA_FORM_OF_EDUCATION_FEE_LINE_CONFIG.forEach(({ field, labels }) => {
    const extractedValue = extractFeeValueFromContent(templateContent, labels);
    if (extractedValue) {
      setValue(field, extractedValue, { shouldDirty: false });
    }
  });

  const certificationEqualizationValue = extractFeeValueFromContent(templateContent, ["Certification and Equalization"]);
  if (certificationEqualizationValue) {
    setValue("certificationEqualization", certificationEqualizationValue, { shouldDirty: false });
  }
};

const applyFeeValuesToLetterContent = (
  html: string,
  values: Record<FeeFieldKey, string | undefined>,
  certificationEqualizationValue?: string,
) => {
  let nextHtml = html;
  FEE_LINE_CONFIG.forEach(({ field, labels }) => {
    const value = values[field];
    if (!value?.trim()) return;
    nextHtml = replaceFeeValueInContent(nextHtml, labels, value);
  });

  if (certificationEqualizationValue?.trim()) {
    nextHtml = replaceFeeValueInContent(nextHtml, ["Certification and Equalization"], certificationEqualizationValue);
  }

  return nextHtml;
};

const today = new Date();
const formatDateForInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const AOPConfig: FC<AOPConfigProps> = ({ country, dataForGeneration, setDataForGeneration, setBodyForPrev, setShowInvoiceInPreview, setWithQRForPreview }) => {
  const registryEntry = ADMISSION_LETTER_BASIC_REGISTRY[country];
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = useForm<IAOPFormValues>({
    defaultValues: {
      addInvoice: false,
      invoiceBody: "",
      currentDate: formatDateForInput(today),
      fullName: "",
      fullNameNat: "",
      dateOfBirth: "",
      nationality: "",
      offeredCourse: "",
      course: "",
      selectedCourse: "",
      degree: "",
      mediumOfInstruction: "",
      duration: "",
      withQR: true,
      ...AOP_ID_SOURCE_DEFAULT_VALUES,
      tuitionFeeFlag: false,
      tuitionFeeVisible: true,
      acommodationVisible: true,
      medicalInsuranceVisible: true,
      residencePermitVisible: true,
      admissionFeeVisible: true,
      otherExpensesVisible: true,
      transferVisible: true,
      certificationEqualizationVisible: true,
      preparatoryDepartmentVisible: false,
      completeOneYearVisible: true,
      showSecondYear: true,
      showTransferFeeNotice: true,
      showAdmissionLetterNotice: true,
      showFirstYearPaymentConfirmation: true,
    },
  });
  const dispatch = useDispatch();
  const generatedDoc = useAdmissionGeneratedDocumentActions({
    getValues,
    setValue: (name, value) => setValue(name as keyof IAOPFormValues, value as never),
    studentIdFormField: "student_id",
    documentType: "admission",
  });
  const [content, setContent] = useState<string>("");
  const [invoiceContent, setInvoiceContent] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const isSyncingFromInputs = useRef(false);
  const isSyncingFromEditor = useRef(false);
  const hasInitializedMaltaIntakeRef = useRef(false);
  const prevFormOfEducationRef = useRef<string | undefined>(undefined);
  const preserveAdmissionSemesterFromTaskRef = useRef(false);
  const applyIdSourceLockRef = useRef<(payload: AdmissionTaskPrefillPayload) => void>(() => {});
  const admissionEditorRef = useRef<TinyMCEEditor | null>(null);
  const invoiceEditorRef = useRef<TinyMCEEditor | null>(null);

  const [isPreviewReady, setIsPreviewReady] = useState(false);

  const offeredCourse = watch("offeredCourse");
  const formOfEducation = watch("formOfEducation");
  const intake = watch("intake");
  const degree = watch("degree");
  const mediumOfInstruction = watch("mediumOfInstruction");
  const admissionSemester = watch("admissionSemester");
  const admissionSemesterDisplay = normalizeAdmissionSemesterValue(admissionSemester);
  const isAdvancedAdmissionLetter = country === "malta" || country === "ukraine" || country === "poland";
  const isMaltaFullTime = isAdvancedAdmissionLetter && formOfEducation === FORM_OF_EDUCATION_FULL_TIME;
  const isMaltaDistance = isAdvancedAdmissionLetter && formOfEducation === FORM_OF_EDUCATION_DISTANCE;
  const isMaltaDynamicLetter = isMaltaFullTime || isMaltaDistance;
  const invoiceBody = watch("invoiceBody");
  const addInvoice = watch("addInvoice");
  const fieldEmail = watch("fieldEmail");
  const userEmail = watch("userEmail");
  const tuitionFee = watch("tuitionFee");
  const medicalInsurance = watch("medicalInsurance");
  const residencePermit = watch("residencePermit");
  const otherExpenses = watch("otherExpenses");
  const acommodation = watch("acommodation");
  const admissionFee = watch("admissionFee");
  const transfer = watch("transfer");
  const completeOneYear = watch("completeOneYear");
  const certificationEqualization = watch("certificationEqualization");
  const preparatoryDepartment = watch("preparatoryDepartment");
  const tuitionFeeVisible = watch("tuitionFeeVisible");
  const acommodationVisible = watch("acommodationVisible");
  const medicalInsuranceVisible = watch("medicalInsuranceVisible");
  const residencePermitVisible = watch("residencePermitVisible");
  const admissionFeeVisible = watch("admissionFeeVisible");
  const otherExpensesVisible = watch("otherExpensesVisible");
  const transferVisible = watch("transferVisible");
  const certificationEqualizationVisible = watch("certificationEqualizationVisible");
  const preparatoryDepartmentVisible = watch("preparatoryDepartmentVisible");
  const completeOneYearVisible = watch("completeOneYearVisible");
  const isPreparatoryDepartmentActive = useMemo(() => {
    const entry = offeredCourse ? MALTA_OFFERED_COURSES_BY_LABEL[offeredCourse] : undefined;
    return !!entry && isMaltaPreparatoryOfferedCourse(entry);
  }, [offeredCourse]);
  const showSecondYear = watch("showSecondYear");
  const showTransferFeeNotice = watch("showTransferFeeNotice");
  const showAdmissionLetterNotice = watch("showAdmissionLetterNotice");
  const showFirstYearPaymentConfirmation = watch("showFirstYearPaymentConfirmation");
  const withQR = watch("withQR");

  useSyncWithQRPreview(withQR, setWithQRForPreview);

  useEffect(() => {
    setShowInvoiceInPreview(!!addInvoice);
    if (!addInvoice) {
      setBodyForPrev((prev) => ({ ...prev, invoiceBody: undefined }));
    }
  }, [addInvoice, setShowInvoiceInPreview, setBodyForPrev]);

  // AUTOCOMPLETE ↓
  const signatureAuto = useAutocomplete(Object.keys(registryEntry.signatures));
  const formOfEducationAuto = useAutocomplete(MALTA_FORM_OF_EDUCATION_LABELS);
  const maltaOfferedCourseAuto = useAutocomplete(MALTA_OFFERED_COURSE_LABELS);
  const degreeAuto = useAutocomplete(STANDARD_DEGREE_LEVEL_VALUES);
  const intakeLabels = useMemo(
    () => buildIntakeOptionsWithStored(isAdvancedAdmissionLetter ? intake : undefined).map((option) => option.label),
    [isAdvancedAdmissionLetter, intake],
  );
  const intakeAuto = useAutocomplete(intakeLabels);
  const invoiceBodyAuto = useAutocomplete(Object.keys(registryEntry.invoiceCourses));
  const nationalitiesAuto = useAutocomplete(Nationalities);
  const whoseDocAuto = useAutocomplete(admissionOfficeWhoseDoc);
  const mediumOfInstructionAuto = useAutocomplete(MEDIUM_OF_INSTRUCTION_LABELS);
  const admissionSemesterAuto = useAutocomplete(ADMISSION_SEMESTER_LABELS);

  const { applyTaskPrefill, lastPrefilledTaskIdRef } = useAdmissionTaskPrefill<IAOPFormValues>({
    setValue,
    country,
    documentSlug: "admission",
    mapping: admissionLetterTaskPrefillMapping,
    onPrefill: (payload, mapping, formBaseline) => {
      generatedDoc.setTaskPrefillSnapshot(payload, mapping, formBaseline);
      syncCourseAndNationalityAutocompletes(payload, maltaOfferedCourseAuto, nationalitiesAuto);
      syncFormOfEducationAutocomplete(payload, formOfEducationAuto);
      syncMediumOfInstructionAutocomplete(payload, mediumOfInstructionAuto);
      preserveAdmissionSemesterFromTaskRef.current = Boolean(
        typeof payload.admissionSemester === "string" && payload.admissionSemester.trim(),
      );
      if (typeof payload.offeredCourse === "string" && payload.offeredCourse.trim()) {
        maltaOfferedCourseAuto.onSelect(payload.offeredCourse);
      }
      if (typeof payload.tuitionFeeFlag === "boolean") {
        setValue("tuitionFeeFlag", payload.tuitionFeeFlag, { shouldDirty: false });
        formBaseline.tuitionFeeFlag = String(payload.tuitionFeeFlag);
      } else {
        formBaseline.tuitionFeeFlag = "false";
      }

      applyIdSourceLockRef.current(payload);
    },
  });

  const idSource = useAOPIdSourceFields<IAOPFormValues>({
    setValue,
    getValues,
    applyTaskPrefill,
    lastPrefilledTaskIdRef,
    fieldEmail: !!fieldEmail,
    userEmail,
  });
  applyIdSourceLockRef.current = idSource.applyIdSourceLockFromPrefill;

  // EDITORS
  useEffect(() => {
    isSyncingFromInputs.current = false;
    isSyncingFromEditor.current = false;
  }, [formOfEducation]);

  useEffect(() => {
    if (!isAdvancedAdmissionLetter) return;

    const resolved = (formOfEducation ?? "").trim();
    if (resolved && formOfEducationAuto.query !== resolved) {
      formOfEducationAuto.onSelect(resolved);
    } else if (!resolved && formOfEducationAuto.query) {
      formOfEducationAuto.onSelect("");
    }
  }, [isAdvancedAdmissionLetter, formOfEducation]);

  useEffect(() => {
    if (!isAdvancedAdmissionLetter) return;

    const resolved = (degree ?? "").trim();
    if (resolved && degreeAuto.query !== resolved) {
      degreeAuto.onSelect(resolved);
    } else if (!resolved && degreeAuto.query) {
      degreeAuto.onSelect("");
    }
  }, [isAdvancedAdmissionLetter, degree]);

  useEffect(() => {
    const resolved = normalizeMediumOfInstructionValue(mediumOfInstruction);
    if (resolved && mediumOfInstructionAuto.query !== resolved) {
      mediumOfInstructionAuto.onSelect(resolved);
    } else if (!resolved && mediumOfInstructionAuto.query) {
      mediumOfInstructionAuto.onSelect("");
    }
  }, [mediumOfInstruction]);

  useEffect(() => {
    if (!isAdvancedAdmissionLetter) return;

    const trimmed = (intake ?? "").trim();
    if (!trimmed) {
      if (!hasInitializedMaltaIntakeRef.current) {
        hasInitializedMaltaIntakeRef.current = true;
        const defaultLabel = getIntakeDisplayLabel(resolveDefaultIntakeValue());
        setValue("intake", defaultLabel, { shouldDirty: false });
        intakeAuto.onSelect(defaultLabel);
      } else if (intakeAuto.query) {
        intakeAuto.onSelect("");
      }
      return;
    }

    hasInitializedMaltaIntakeRef.current = true;
    const displayLabel = getIntakeDisplayLabel(trimmed, buildIntakeOptionsWithStored(trimmed));
    if (displayLabel && intakeAuto.query !== displayLabel) {
      intakeAuto.onSelect(displayLabel);
    }
  }, [isAdvancedAdmissionLetter, intake]);

  useEffect(() => {
    if (isMaltaDynamicLetter) {
      return;
    }
    if (!formOfEducation) {
      setContent("");
    }
  }, [formOfEducation, isMaltaDynamicLetter]);

  useEffect(() => {
    if (!isAdvancedAdmissionLetter) return;

    const previousFormOfEducation = prevFormOfEducationRef.current;
    prevFormOfEducationRef.current = formOfEducation;

    if (!formOfEducation) {
      if (previousFormOfEducation) {
        applyMaltaFormOfEducationUnselectedDefaults((name, value, options) => {
          setValue(name as keyof IAOPFormValues, value as IAOPFormValues[keyof IAOPFormValues], options);
        });
        setValue("offeredCourse", "", { shouldDirty: false });
        maltaOfferedCourseAuto.onSelect("");
        clearMaltaOfferedCourseSelection(setValue);
      }
      return;
    }

    const templateContent = templatesMaltaEnFormOfEducationSettings[formOfEducation]?.tinyMCEContent ?? "";
    const offeredCourseApplyOptions = {
      updateAdmissionSemester: !preserveAdmissionSemesterFromTaskRef.current,
    };

    if (formOfEducation === FORM_OF_EDUCATION_FULL_TIME) {
      applyMaltaFullTimeOnCampusFormDefaults((name, value, options) => {
        setValue(name as keyof IAOPFormValues, value as IAOPFormValues[keyof IAOPFormValues], options);
      });
    } else if (formOfEducation === FORM_OF_EDUCATION_DISTANCE) {
      applyMaltaDistanceOnlineFormDefaults((name, value, options) => {
        setValue(name as keyof IAOPFormValues, value as IAOPFormValues[keyof IAOPFormValues], options);
      });
    } else {
      populateMaltaFormOfEducationFeeFields(templateContent, setValue);
    }

    const selectedOfferedCourse = (offeredCourse ?? "").trim();
    if (selectedOfferedCourse) {
      const entry = MALTA_OFFERED_COURSES_BY_LABEL[selectedOfferedCourse];
      if (entry) {
        applyMaltaOfferedCourseSelection(entry, setValue, offeredCourseApplyOptions);
      }
    }
  }, [isAdvancedAdmissionLetter, formOfEducation, offeredCourse, setValue]);

  useEffect(() => {
    if (!isAdvancedAdmissionLetter) return;

    if (!offeredCourse) {
      applyMaltaOfferedCourseCertificationEqualization(undefined, (name, value, options) => {
        setValue(name as keyof IAOPFormValues, value as IAOPFormValues[keyof IAOPFormValues], options);
      });
      applyMaltaOfferedCoursePreparatoryDepartment(undefined, (name, value, options) => {
        setValue(name as keyof IAOPFormValues, value as IAOPFormValues[keyof IAOPFormValues], options);
      });
      return;
    }

    const entry = MALTA_OFFERED_COURSES_BY_LABEL[offeredCourse];
    if (entry) {
      applyMaltaOfferedCourseSelection(
        entry,
        (name, value, options) => {
          setValue(name as keyof IAOPFormValues, value as IAOPFormValues[keyof IAOPFormValues], options);
        },
        { updateAdmissionSemester: !preserveAdmissionSemesterFromTaskRef.current },
      );
    }

    preserveAdmissionSemesterFromTaskRef.current = false;
  }, [isAdvancedAdmissionLetter, offeredCourse, setValue]);

  useEffect(() => {
    if (!isAdvancedAdmissionLetter || !formOfEducation) return;

    const total = isMaltaDistance
      ? calcMaltaDistanceFirstYearTotal(
          {
            tuitionFee,
            admissionFee,
            otherExpenses,
            certificationEqualization,
            preparatoryDepartment,
          },
          {
            tuitionFee: !!tuitionFeeVisible,
            admissionFee: !!admissionFeeVisible,
            otherExpenses: !!otherExpensesVisible,
            certificationEqualization: !!certificationEqualizationVisible,
            preparatoryDepartment: !!preparatoryDepartmentVisible,
          },
        )
      : isMaltaFullTime
        ? calcMaltaFullTimeFirstYearTotal(
            {
              tuitionFee,
              acommodation,
              medicalInsurance,
              residencePermit,
              admissionFee,
              otherExpenses,
              transfer,
              certificationEqualization,
              preparatoryDepartment,
              completeOneYear,
            },
            {
              tuitionFee: !!tuitionFeeVisible,
              acommodation: !!acommodationVisible,
              medicalInsurance: !!medicalInsuranceVisible,
              residencePermit: !!residencePermitVisible,
              admissionFee: !!admissionFeeVisible,
              otherExpenses: !!otherExpensesVisible,
              transfer: !!transferVisible,
              certificationEqualization: !!certificationEqualizationVisible,
              preparatoryDepartment: !!preparatoryDepartmentVisible,
              completeOneYear: false,
            },
          )
        : 0;

    if (!total) return;

    const formattedTotal = formatMaltaMoneyValue(total);
    const nextCompleteOneYear = formattedTotal || "";
    const currentCompleteOneYear = getValues("completeOneYear")?.trim() || "";
    if (nextCompleteOneYear && currentCompleteOneYear !== nextCompleteOneYear) {
      setValue("completeOneYear", nextCompleteOneYear, { shouldDirty: true });
    }
  }, [
    isAdvancedAdmissionLetter,
    formOfEducation,
    isMaltaDistance,
    isMaltaFullTime,
    tuitionFee,
    acommodation,
    medicalInsurance,
    residencePermit,
    admissionFee,
    otherExpenses,
    transfer,
    certificationEqualization,
    preparatoryDepartment,
    tuitionFeeVisible,
    acommodationVisible,
    medicalInsuranceVisible,
    residencePermitVisible,
    admissionFeeVisible,
    otherExpensesVisible,
    transferVisible,
    certificationEqualizationVisible,
    preparatoryDepartmentVisible,
    getValues,
    setValue,
  ]);

  useEffect(() => {
    if (!isMaltaFullTime) return;

    const nextContent = buildMaltaFullTimeAdmissionBody({
      intake,
      feeValues: {
        tuitionFee,
        acommodation,
        medicalInsurance,
        residencePermit,
        admissionFee,
        otherExpenses,
        transfer,
        certificationEqualization,
        preparatoryDepartment,
        completeOneYear,
      },
      feeVisibility: {
        tuitionFee: !!tuitionFeeVisible,
        acommodation: !!acommodationVisible,
        medicalInsurance: !!medicalInsuranceVisible,
        residencePermit: !!residencePermitVisible,
        admissionFee: !!admissionFeeVisible,
        otherExpenses: !!otherExpensesVisible,
        transfer: !!transferVisible,
        certificationEqualization: !!certificationEqualizationVisible,
        preparatoryDepartment: !!preparatoryDepartmentVisible,
        completeOneYear: true,
      },
      showSecondYear: !!showSecondYear,
      noticeVisibility: {
        transferFeeNotice: !!showTransferFeeNotice,
        admissionLetterNotice: !!showAdmissionLetterNotice,
        firstYearPaymentConfirmation: !!showFirstYearPaymentConfirmation,
      },
    });

    isSyncingFromInputs.current = true;
    setContent(nextContent);
  }, [
    isMaltaFullTime,
    intake,
    tuitionFee,
    acommodation,
    medicalInsurance,
    residencePermit,
    admissionFee,
    otherExpenses,
    transfer,
    certificationEqualization,
    preparatoryDepartment,
    completeOneYear,
    tuitionFeeVisible,
    acommodationVisible,
    medicalInsuranceVisible,
    residencePermitVisible,
    admissionFeeVisible,
    otherExpensesVisible,
    transferVisible,
    certificationEqualizationVisible,
    preparatoryDepartmentVisible,
    completeOneYearVisible,
    showSecondYear,
    showTransferFeeNotice,
    showAdmissionLetterNotice,
    showFirstYearPaymentConfirmation,
  ]);

  useEffect(() => {
    if (!isMaltaDistance) return;

    const nextContent = buildMaltaDistanceAdmissionBody({
      intake,
      feeValues: {
        tuitionFee,
        admissionFee,
        otherExpenses,
        certificationEqualization,
        preparatoryDepartment,
      },
      feeVisibility: {
        tuitionFee: !!tuitionFeeVisible,
        admissionFee: !!admissionFeeVisible,
        otherExpenses: !!otherExpensesVisible,
        certificationEqualization: !!certificationEqualizationVisible,
        preparatoryDepartment: !!preparatoryDepartmentVisible,
      },
      completeOneYear,
      noticeVisibility: {
        transferFeeNotice: !!showTransferFeeNotice,
        admissionLetterNotice: !!showAdmissionLetterNotice,
        firstYearPaymentConfirmation: !!showFirstYearPaymentConfirmation,
      },
    });

    isSyncingFromInputs.current = true;
    setContent(nextContent);
  }, [
    isMaltaDistance,
    intake,
    tuitionFee,
    admissionFee,
    otherExpenses,
    certificationEqualization,
    preparatoryDepartment,
    tuitionFeeVisible,
    admissionFeeVisible,
    otherExpensesVisible,
    certificationEqualizationVisible,
    preparatoryDepartmentVisible,
    completeOneYear,
    showTransferFeeNotice,
    showAdmissionLetterNotice,
    showFirstYearPaymentConfirmation,
  ]);

  useEffect(() => {
    if (!isAdvancedAdmissionLetter || isMaltaDynamicLetter || !formOfEducation) return;

    const templateContent = templatesMaltaEnFormOfEducationSettings[formOfEducation]?.tinyMCEContent ?? "";
    if (!templateContent) {
      setContent("");
      return;
    }

    const nextContent = applyFeeValuesToLetterContent(
      templateContent,
      {
        tuitionFee,
        medicalInsurance,
        residencePermit,
        otherExpenses,
        acommodation,
        admissionFee,
        transfer,
        completeOneYear,
      },
      certificationEqualization,
    );

    isSyncingFromInputs.current = true;
    setContent(nextContent);
  }, [
    isAdvancedAdmissionLetter,
    isMaltaDynamicLetter,
    formOfEducation,
    tuitionFee,
    medicalInsurance,
    residencePermit,
    otherExpenses,
    acommodation,
    admissionFee,
    transfer,
    completeOneYear,
    certificationEqualization,
  ]);

  useEffect(() => {
    if (!invoiceBody) {
      setInvoiceContent("");
      return;
    }
    const invoiceSettings = ADMISSION_LETTER_BASIC_REGISTRY[country].invoiceCourses[invoiceBody];
    setInvoiceContent(invoiceSettings ? invoiceSettings.tinyMCEContent : "");
  }, [invoiceBody, country]);

  const generateNewDocument = async (values: IAOPFormValues) => {
    generatedDoc.resetGeneratedState();
    dispatch(setIsPreloyder(true));
    try {
      const taskReady = await generatedDoc.ensureTaskReadyForDocumentGenerate();
      if (!taskReady) return;

      const signatureKey = values.signature;
      const signatureValue = registryEntry.signatures[signatureKey];
      let response;
      if (country === "malta") {
        if (addInvoice) {
          response = await admissionOfficeRequests.createAdmissionInvoiceDoc(dataForGeneration);
        } else {
          response = await admissionOfficeRequests.createAdmissionDoc(dataForGeneration, signatureValue);
        }
      } else if (country === "poland") {
        const data = dataForGeneration as AOPPolandAdmissionData;
        if (addInvoice) {
          response = await admissionOfficeRequests.createPolandAdmissionInvoiceDoc(data);
        } else {
          response = await admissionOfficeRequests.createPolandAdmissionDoc(data, signatureValue);
        }
      } else {
        const data = dataForGeneration as AOPUkraineAdmissionData;
        if (addInvoice) {
          response = await admissionOfficeRequests.createUkraineAdmissionInvoiceDoc(data);
        } else {
          response = await admissionOfficeRequests.createUkraineAdmissionDoc(data, signatureValue);
        }
      }
      console.log("response", response);
      generatedDoc.onDocumentGenerated(response);
      setIsPreviewReady(false);
    } catch (error) {
      alert("An unexpected error occurred - write to the developers");
    } finally {
      dispatch(setIsPreloyder(false));
    }
  };

  const handlePreview = () => {
    const previewAdmissionContent = admissionEditorRef.current?.getContent() ?? content;
    const previewInvoiceContent = invoiceEditorRef.current?.getContent() ?? invoiceContent;

    if (previewAdmissionContent !== content) {
      setContent(previewAdmissionContent);
    }
    if (previewInvoiceContent !== invoiceContent) {
      setInvoiceContent(previewInvoiceContent);
    }

    const watchedFields = {
      coursePrefix: watch("coursePrefix"),
      selectedCourse: watch("selectedCourse"),
      degree: watch("degree"),
      duration: watch("duration"),
      fullName: watch("fullName"),
      mediumOfInstruction: watch("mediumOfInstruction"),
      nationality: watch("nationality"),
      title: title,
      whose_doc: watch("whose_doc"),
      currentDate: watch("currentDate"),
      dateOfBirth: watch("dateOfBirth"),
      addInvoice: watch("addInvoice"),
      withQR: watch("withQR"),
    };
    const joinedSelectedCourse = joinSelectedCourse(watchedFields.degree, watchedFields.selectedCourse);
    setDataForGeneration((prev) => ({
      ...prev,
      course: joinedSelectedCourse || "",
      coursePrefix: watchedFields.coursePrefix || "",
      duration: watchedFields.duration || "",
      fullName: watchedFields.fullName || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      nationality: watchedFields.nationality || "",
      title: watchedFields.title || "",
      whose_doc: watchedFields.whose_doc || "",
      need_qr: !!watchedFields.withQR,
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      body: splitHtmlByBlocks(previewAdmissionContent),
      invoiceBody: addInvoice ? splitHtmlByBlocks(previewInvoiceContent) : undefined,
    }));
    setBodyForPrev((prev) => ({
      ...prev,
      course: joinedSelectedCourse || "",
      coursePrefix: watchedFields.coursePrefix || "",
      duration: watchedFields.duration || "",
      fullName: watchedFields.fullName || "",
      mediumOfInstruction: watchedFields.mediumOfInstruction || "",
      nationality: watchedFields.nationality || "",
      title: watchedFields.title || "",
      whose_doc: watchedFields.whose_doc || "",
      currentDate: watchedFields.currentDate ? parseDate(watchedFields.currentDate) : "",
      dateOfBirth: watchedFields.dateOfBirth ? parseDate(watchedFields.dateOfBirth) : "",
      body: splitHtmlByBlocksWithFontConvert(previewAdmissionContent),
      invoiceBody: addInvoice ? splitHtmlByBlocksWithFontConvert(previewInvoiceContent) : undefined,
    }));
    // if()
    const hasContent = previewAdmissionContent.trim().length > 0 || previewInvoiceContent.trim().length > 0;
    if (hasContent) {
      setIsPreviewReady(true);
      generatedDoc.resetGeneratedState();
    }
  };

  const handleEditorChange = (newContent: string) => {
    setContent(newContent);

    if (isMaltaDynamicLetter || isSyncingFromInputs.current) {
      if (isSyncingFromInputs.current) {
        isSyncingFromInputs.current = false;
      }
      return;
    }

    isSyncingFromEditor.current = true;
    FEE_LINE_CONFIG.forEach(({ field, labels }) => {
      const extractedValue = extractFeeValueFromContent(newContent, labels);
      if (extractedValue) {
        const currentValue = getValues(field);
        if (currentValue !== extractedValue) {
          setValue(field, extractedValue, { shouldDirty: true });
        }
      }
    });
    isSyncingFromEditor.current = false;
  };
  const handleInvoiceEditorChange = (newInvoiceContent: string) => setInvoiceContent(newInvoiceContent);

  function parseDate(value: string) {
    const [year, month, day] = value.split("-");
    return `${day}.${month}.${year}`;
  }

  const handleCopy = async (fieldName: string) => {
    const value = getValues(fieldName as keyof IAOPFormValues);
    if (!value) return;

    try {
      await navigator.clipboard.writeText(String(value));
    } catch (err) {
      console.error("Ошибка копирования", err);
    }
  };

  return (
    <>
      <div className="admission-office-page-config">
        <div className="admission-office-page-config-logo">
          <div className="logo-svg">
            <LogoSvg />
          </div>
          <div className="logo-line"></div>
          <p>Platforma</p>
        </div>

        <form className="admission-office-page-config-form">
          <div className="admission-office-page-config-title-row">
            <p className="admission-office-page-config-title">ADMISSION/CONFIRMATION LETTER</p>
          </div>

          <AOPIdSourceRow
            control={control}
            register={register}
            setError={setError}
            clearErrors={clearErrors}
            applyTaskPrefill={idSource.applyTaskPrefill}
            lastPrefilledTaskIdRef={lastPrefilledTaskIdRef}
            lastPrefilledStudentIdRef={idSource.lastPrefilledStudentIdRef}
            fieldEmail={!!fieldEmail}
            isTaskIdDisabled={idSource.isTaskIdDisabled}
            isStudentIdDisabled={idSource.isStudentIdDisabled}
            isFieldEmailDisabled={idSource.isFieldEmailDisabled}
            onClearTaskId={idSource.handleClearTaskId}
            onClearStudentId={idSource.handleClearStudentId}
            onClearUserEmail={idSource.handleClearUserEmail}
            onCheckUserEmail={() => void idSource.handleCheckUserEmail()}
            onCopy={idSource.handleCopyIdField}
          />

          <div className="form-group-fields gap">
            <div className="form-group-fields-input whose_doc">
              <AutocompleteInput
                placeholder="Whose"
                value={whoseDocAuto.query}
                onChange={whoseDocAuto.onChange}
                onSelect={(v) => {
                  whoseDocAuto.onSelect(v);
                  setValue("whose_doc", v);
                }}
                isOpen={whoseDocAuto.isOpen}
                options={whoseDocAuto.filtered}
                error={!!errors.invoiceBody}
                  inputProps={{
                  register: register("whose_doc"),
                  close: whoseDocAuto.close,
                  setOpen: whoseDocAuto.onClick,
                  onArrowClick: whoseDocAuto.onArrowClick,
                }}
                identifier={"whose"}
              />
            </div>
            {/* <div className="form-group-fields-input whose_doc">
            <div className="form-group-fields-input-label">
              <label htmlFor="whose_doc">Whose</label>
              <div className="form-group-fields-input-label-line"></div>
            </div>

            <input
              className="whose_doc"
              id="whose_doc"
              type="text"
              {...register("whose_doc")}
              placeholder="Whose"
            />
            <div className="form-group-fields-input-svg">
              <ArrowSvg />
            </div>
          </div> */}
            <div className="form-group-fields-input coursePrefix">
              {/* <div className="form-group-fields-input-label">
              <label htmlFor="coursePrefix">Whose</label>
              <div className="form-group-fields-input-label-line"></div>
            </div> */}
              <input className="coursePrefix" id="coursePrefix" type="text" {...register("coursePrefix")} />
              {/* <div className="form-group-fields-input-svg">
              <ArrowSvg />
            </div> */}
            </div>

            <div className="form-group-fields-invoice">
              <Controller
                name="addInvoice"
                control={control}
                render={({ field }) => <CustomSwitch label="Add Invoice" value={field.value} onChange={field.onChange} />}
              />
            </div>

            <div className="form-group-fields-input currentDate">
              <div className="form-group-fields-input-label">
                <label htmlFor="currentDate">
                  Date of Issue
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>

              <input className="currentDate" id="currentDate" type="date" {...register("currentDate")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name">
                  Full Name<span>*</span>
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame" id="full-mame" type="text" {...register("fullName", { required: true })} />
              {errors.fullName && <p>This field is required</p>}
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullName")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="full-name-nat">
                  Full Name (Nat.)
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="full-mame-nat" id="full-mame-nat" type="text" {...register("fullNameNat")} />
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-copy" onClick={() => handleCopy("fullNameNat")}>
                  <AODSvgExport.FullNameCopySvg />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group-fields">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="dateOfBirth">
                  Data of Birth
                </label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input className="dateOfBirth" id="dateOfBirth" type="date" {...register("dateOfBirth")} />
              <div className="form-group-fields-input-svg">
                <AODSvgExport.DateInputSvg />
              </div>
            </div>
            <AutocompleteInput
              label="Nationality"
              value={nationalitiesAuto.query}
              onChange={nationalitiesAuto.onChange}
              onSelect={(v) => {
                nationalitiesAuto.onSelect(v);
                setValue("nationality", v);
              }}
              isOpen={nationalitiesAuto.isOpen}
              options={nationalitiesAuto.filtered}
              error={!!errors.nationality}
                inputProps={{
                register: register("nationality"),
                close: nationalitiesAuto.close,
                setOpen: nationalitiesAuto.onClick,
                onArrowClick: nationalitiesAuto.onArrowClick,
              }}
              identifier={"nationality"}
            />
            {/* 
            <div className="form-group-fields-input-label">
              <label htmlFor="nationality">
                Nationality
              </label>
              <div className="form-group-fields-input-label-line"></div>
            </div>
            <input
              className="nationality"
              id="nationality"
              type="text"
              {...register("nationality")}
            />
            <div className="form-group-fields-input-svg">
              <ArrowSvg />
            </div> 
          */}
          </div>
          <>
              <div className="form-group-fields form-group-fields-half">
                <div className="form-group-fields-input course">
                  <AutocompleteInput
                    label="Degree"
                    value={degreeAuto.query}
                    onChange={degreeAuto.onChange}
                    onSelect={(v) => {
                      degreeAuto.onSelect(v);
                      setValue("degree", v);
                    }}
                    isOpen={degreeAuto.isOpen}
                    options={degreeAuto.filtered}
                    error={!!errors.degree}
                    inputProps={{
                      register: register("degree"),
                      close: degreeAuto.close,
                      setOpen: degreeAuto.onClick,
                      onArrowClick: degreeAuto.onArrowClick,
                      setValue,
                      clearErrors,
                      name: "degree",
                    }}
                    identifier="malta-degree-auto"
                  />
                </div>
                <div className="form-group-fields-input">
                  <div className="form-group-fields-input-label">
                    <label htmlFor="selected-сourse">
                      Selected course
                    </label>
                    <div className="form-group-fields-input-label-line"></div>
                  </div>
                  <input className="selected-сourse" id="selected-сourse" type="text" {...register("selectedCourse")} />
                </div>
              </div>
              <div className="form-group-fields form-group-fields-half">
                <div className="form-group-fields-input">
                  <div className="form-group-fields-input-label">
                    <label htmlFor="category">
                      Category
                    </label>
                    <div className="form-group-fields-input-label-line"></div>
                  </div>
                  <input className="category" id="category" type="text" {...register("category")} />
                </div>
                <div className="form-group-fields-input course">
                  <AutocompleteInput
                    label="Admission Semester"
                    value={admissionSemesterDisplay}
                    onChange={(v) => {
                      admissionSemesterAuto.onChange(v);
                      setValue("admissionSemester", v, { shouldDirty: true });
                    }}
                    onSelect={(v) => {
                      admissionSemesterAuto.onSelect(v);
                      setValue("admissionSemester", v, { shouldDirty: true });
                    }}
                    isOpen={admissionSemesterAuto.isOpen}
                    options={admissionSemesterAuto.filtered}
                    error={!!errors.admissionSemester}
                    inputProps={{
                      register: register("admissionSemester"),
                      close: admissionSemesterAuto.close,
                      setOpen: admissionSemesterAuto.onClick,
                      onArrowClick: admissionSemesterAuto.onArrowClick,
                      setValue,
                      clearErrors,
                      name: "admissionSemester",
                    }}
                    identifier="admission-semester-auto"
                  />
                </div>
              </div>
              <div className="form-group-fields form-group-fields-half">
                <div className="form-group-fields-input course">
                  <AutocompleteInput
                    label="Medium of Instruction"
                    value={mediumOfInstructionAuto.query}
                    onChange={mediumOfInstructionAuto.onChange}
                    onSelect={(v) => {
                      mediumOfInstructionAuto.onSelect(v);
                      setValue("mediumOfInstruction", v);
                    }}
                    isOpen={mediumOfInstructionAuto.isOpen}
                    options={mediumOfInstructionAuto.filtered}
                    error={!!errors.mediumOfInstruction}
                    inputProps={{
                      register: register("mediumOfInstruction"),
                      close: mediumOfInstructionAuto.close,
                      setOpen: mediumOfInstructionAuto.onClick,
                      onArrowClick: mediumOfInstructionAuto.onArrowClick,
                      setValue,
                      clearErrors,
                      name: "mediumOfInstruction",
                    }}
                    identifier="medium-of-instruction-auto"
                  />
                </div>
                <div className="form-group-fields-input">
                  <div className="form-group-fields-input-label">
                    <label htmlFor="duration">
                      Duration
                    </label>
                    <div className="form-group-fields-input-label-line"></div>
                  </div>
                  <input className="duration" id="duration" type="text" {...register("duration")} />
                </div>
              </div>
              <div className="form-group-fields">
                <div className="form-group-fields-input course">
                  <AutocompleteInput
                    label="Form of Education"
                    value={formOfEducationAuto.query}
                    onChange={formOfEducationAuto.onChange}
                    onSelect={(v) => {
                      formOfEducationAuto.onSelect(v);
                      setValue("formOfEducation", v);
                    }}
                    isOpen={formOfEducationAuto.isOpen}
                    options={formOfEducationAuto.filtered}
                    error={!!errors.formOfEducation}
                    inputProps={{
                      register: register("formOfEducation"),
                      close: formOfEducationAuto.close,
                      setOpen: formOfEducationAuto.onClick,
                      onArrowClick: formOfEducationAuto.onArrowClick,
                      setValue,
                      clearErrors,
                      name: "formOfEducation",
                    }}
                    identifier="form-of-education-auto"
                  />
                </div>
              </div>
              <div className="form-group-fields">
                <div className="form-group-fields-input course">
                  <AutocompleteInput
                    label="Select the offered course"
                    value={maltaOfferedCourseAuto.query}
                    onChange={maltaOfferedCourseAuto.onChange}
                    onSelect={(v) => {
                      maltaOfferedCourseAuto.onSelect(v);
                      setValue("offeredCourse", v);
                    }}
                    isOpen={maltaOfferedCourseAuto.isOpen}
                    options={maltaOfferedCourseAuto.filtered}
                    error={!!errors.offeredCourse}
                    inputProps={{
                      register: register("offeredCourse"),
                      close: maltaOfferedCourseAuto.close,
                      setOpen: maltaOfferedCourseAuto.onClick,
                      onArrowClick: maltaOfferedCourseAuto.onArrowClick,
                      setValue,
                      clearErrors,
                      name: "offeredCourse",
                    }}
                    identifier={"malta-offered-course-auto"}
                    clearButton
                  />
                </div>
              </div>
              <div className="form-group-fields form-group-fields-half">
                <div className="form-group-fields-input course">
                  <AutocompleteInput
                    label="Intake"
                    value={intakeAuto.query}
                    onChange={intakeAuto.onChange}
                    onSelect={(v) => {
                      intakeAuto.onSelect(v);
                      setValue("intake", v);
                    }}
                    isOpen={intakeAuto.isOpen}
                    options={intakeAuto.filtered}
                    error={!!errors.intake}
                    allowAdd={true}
                    onAddItem={(label) => {
                      const trimmed = label.trim();
                      if (!trimmed) return;
                      intakeAuto.onSelect(trimmed);
                      setValue("intake", trimmed);
                    }}
                    inputProps={{
                      register: register("intake"),
                      close: intakeAuto.close,
                      setOpen: intakeAuto.onClick,
                      onArrowClick: intakeAuto.onArrowClick,
                      setValue,
                      clearErrors,
                      name: "intake",
                    }}
                    identifier="malta-intake-auto"
                  />
                </div>
                <MaltaFullTimeFeeField
                  control={control}
                  name="tuitionFee"
                  visibilityName="tuitionFeeVisible"
                  flagName="tuitionFeeFlag"
                  flagLetter="S"
                  flagAriaLabel="Tuition Fee scholarship flag"
                  label="Tuition Fee"
                  inputClassName="tuitionFee"
                  inputId="tuitionFee"
                />
              </div>
              <div className="form-group-fields form-group-fields-half">
                <MaltaFullTimeFeeField
                  control={control}
                  name="acommodation"
                  visibilityName="acommodationVisible"
                  label="Acommodation"
                  inputClassName="acommodation"
                  inputId="acommodation"
                />
                <MaltaFullTimeFeeField
                  control={control}
                  name="medicalInsurance"
                  visibilityName="medicalInsuranceVisible"
                  label="Medical Insurance"
                  inputClassName="medicalInsurance"
                  inputId="medicalInsurance"
                />
              </div>
              <div className="form-group-fields form-group-fields-half">
                <MaltaFullTimeFeeField
                  control={control}
                  name="residencePermit"
                  visibilityName="residencePermitVisible"
                  label="Residence Permit"
                  inputClassName="residencePermit"
                  inputId="residencePermit"
                />
                <MaltaFullTimeFeeField
                  control={control}
                  name="admissionFee"
                  visibilityName="admissionFeeVisible"
                  label="Admission Fee"
                  inputClassName="admissionFee"
                  inputId="admissionFee"
                />
              </div>
              <div className="form-group-fields form-group-fields-half">
                <MaltaFullTimeFeeField
                  control={control}
                  name="otherExpenses"
                  visibilityName="otherExpensesVisible"
                  label="Other Expenses"
                  inputClassName="otherExpenses"
                  inputId="otherExpenses"
                />
                <MaltaFullTimeFeeField
                  control={control}
                  name="transfer"
                  visibilityName="transferVisible"
                  label="Transfer"
                  inputClassName="transfer"
                  inputId="transfer"
                />
              </div>
              <div className="form-group-fields form-group-fields-full">
                <MaltaFullTimeFeeField
                  control={control}
                  name="certificationEqualization"
                  visibilityName="certificationEqualizationVisible"
                  label="Certification & Equalization"
                  inputClassName="certificationEqualization"
                  inputId="certificationEqualization"
                />
              </div>
              <div className="form-group-fields form-group-fields-full">
                <MaltaFullTimeFeeField
                  control={control}
                  name="preparatoryDepartment"
                  visibilityName="preparatoryDepartmentVisible"
                  label="Preparatory Department"
                  inputClassName="preparatoryDepartment"
                  inputId="preparatoryDepartment"
                  disabled={!isPreparatoryDepartmentActive}
                />
              </div>
              <div className="form-group-fields form-group-fields-complete-one-year-row">
                <MaltaFullTimeFeeField
                  control={control}
                  name="completeOneYear"
                  label="Complete One Year"
                  inputClassName="completeOneYear"
                  inputId="completeOneYear"
                  readOnly
                  hideVisibilityToggle
                />
                <div className="form-group-fields-second-year">
                  <Controller
                    name="showSecondYear"
                    control={control}
                    render={({ field }) => <CustomSwitch label="Second year" value={field.value} onChange={field.onChange} />}
                  />
                </div>
              </div>
              <label>Options Notices:</label>
              <div className="malta-important-notices">
                <div className="malta-notice-switch">
                  <Controller
                    name="showTransferFeeNotice"
                    control={control}
                    render={({ field }) => (
                      <CustomSwitch label="Transfer Fee" value={field.value} onChange={field.onChange} />
                    )}
                  />
                </div>
                <div className="malta-notice-switch">
                  <Controller
                    name="showAdmissionLetterNotice"
                    control={control}
                    render={({ field }) => (
                      <CustomSwitch label="Additional Expenses" value={field.value} onChange={field.onChange} />
                    )}
                  />
                </div>
                <div className="malta-notice-switch">
                  <Controller
                    name="showFirstYearPaymentConfirmation"
                    control={control}
                    render={({ field }) => (
                      <CustomSwitch label="First-Year Payment" value={field.value} onChange={field.onChange} />
                    )}
                  />
                </div>
              </div>
            </>
          {addInvoice && (
            <div className="form-group-fields">
              <div className="form-group-fields-input invoiceBody">
                <AutocompleteInput
                  label="Invoice details"
                  value={invoiceBodyAuto.query}
                  onChange={invoiceBodyAuto.onChange}
                  onSelect={(v) => {
                    invoiceBodyAuto.onSelect(v);
                    setValue("invoiceBody", v);
                  }}
                  isOpen={invoiceBodyAuto.isOpen}
                  options={invoiceBodyAuto.filtered}
                  error={!!errors.invoiceBody}
                    inputProps={{
                    register: register("invoiceBody"),
                    close: invoiceBodyAuto.close,
                    setOpen: invoiceBodyAuto.onClick,
                    onArrowClick: invoiceBodyAuto.onArrowClick,
                  }}
                  identifier={"invoice"}
                />
              </div>
            </div>
          )}

          <label>Admission body</label>
          <Editor
            key={`admission-${formOfEducation || "empty"}`}
            value={content}
            onInit={(_evt, editor) => {
              admissionEditorRef.current = editor;
            }}
            init={{
              height: TINYMCE_EDITOR_HEIGHT,
              menubar: false,
              plugins: ["lists", "link", "code", "table"],
              toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
              font_size_formats: TINYMCE_FONT_SIZE_FORMATS,
              branding: false,
              toolbar_mode: "wrap",
              licenseKey: "gpl",
              license_key: "gpl" as any,
              content_style: TINYMCE_EDITOR_CONTENT_STYLE,
            }}
            onEditorChange={handleEditorChange}
            tinymceScriptSrc="/tinymce/tinymce.min.js"
          />

          {addInvoice && (
            <>
              <label>Invoice body</label>
              <Editor
                value={invoiceContent}
                onInit={(_evt, editor) => {
                  invoiceEditorRef.current = editor;
                }}
                init={{
                  height: TINYMCE_EDITOR_HEIGHT,
                  menubar: false,
                  plugins: ["lists", "link", "code", "table"],
                  toolbar: "undo redo fontsize bold italic underline align lineheight tinycomments numlist bullist fontfamily forecolor",
                  font_size_formats: TINYMCE_FONT_SIZE_FORMATS,
                  branding: false,
                  toolbar_mode: "wrap",
                  licenseKey: "gpl",
                  license_key: "gpl" as any,
                  content_style: TINYMCE_EDITOR_CONTENT_STYLE,
                }}
                onEditorChange={handleInvoiceEditorChange}
                tinymceScriptSrc="/tinymce/tinymce.min.js"
              />
            </>
          )}

          <AOPGeneratedDocumentToolbar
            isGenerated={generatedDoc.isGenerated}
            generatedPdfUrl={generatedDoc.generatedPdfUrl}
            taskAttachedPdfUrl={generatedDoc.taskAttachedPdfUrl}
            previewPdfUrl={generatedDoc.previewPdfUrl}
            canOpenPdfPreview={generatedDoc.canOpenPdfPreview}
            canOpenTaskDocumentEye={generatedDoc.canOpenTaskDocumentEye}
            isGeneratedPdfPreviewOpen={generatedDoc.isGeneratedPdfPreviewOpen}
            setIsGeneratedPdfPreviewOpen={generatedDoc.setIsGeneratedPdfPreviewOpen}
            generatedPreviewTitle={generatedDoc.generatedPreviewTitle}
            onDownload={generatedDoc.handleDownloadGeneratedPdf}
            onApprove={generatedDoc.handlePreviewApprove}
            onReject={generatedDoc.handlePreviewReject}
            isTaskDocumentCheckReview={generatedDoc.isTaskDocumentCheckReview}
            previewButton={
              <BtnDefault onClick={handlePreview} reverse={true}>
                <p>Preview doc</p>
                <AODSvgExport.PreviewSvg />
              </BtnDefault>
            }
            generateButton={
              <BtnDefault onClick={handleSubmit(generateNewDocument)} disabled={!isPreviewReady}>
                <p>Generate doc</p>
                <AODSvgExport.GenerateSvg />
              </BtnDefault>
            }
          />
          <div className="form-group-fields form-group-fields-signature-row">
            <div className="form-group-fields-input signature">
              <AutocompleteInput
                label="Signature"
                value={signatureAuto.query}
                defaultValue="Fedorova Anzhelika"
                onChange={signatureAuto.onChange}
                onSelect={(v) => {
                  signatureAuto.onSelect(v);
                  setValue("signature", v);
                }}
                isOpen={signatureAuto.isOpen}
                options={signatureAuto.filtered}
                error={!!errors.signature}
                  inputProps={{
                  register: register("signature"),
                  close: signatureAuto.close,
                  setOpen: signatureAuto.onClick,
                  onArrowClick: signatureAuto.onArrowClick,
                }}
                identifier={"signature"}
              />
            </div>
            {country !== "poland" && (
              <div className="form-group-fields-invoice">
                <Controller
                  name="withQR"
                  control={control}
                  render={({ field }) => <CustomSwitch label="With QR" value={field.value} onChange={field.onChange} />}
                />
              </div>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default AOPConfig;
